import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot, Camera, Eye, Mic, Save, ScanFace, ShieldCheck, Trash2, Volume2,
  Activity, Clock, UserCheck, Layers, Settings2
} from "lucide-react";
import { mergeCommandBrain } from "../utils/nexoCommandBrain";
import { getAnomalyLog, clearAnomalyLog, getTopUnrecognisedCandidates } from "../utils/nexoSelfLearningEngine";

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";
const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

function getLoginUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
}

export default function NexoAssistant() {
  const token = localStorage.getItem(TOKEN_KEY);
  const user = getLoginUser();
  const isAdmin = user?.role === "admin";
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const voiceStreamRef = useRef(null);

  const [settings, setSettings] = useState({});
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [faceProfiles, setFaceProfiles] = useState([]);
  const [faceScans, setFaceScans] = useState([]);
  const [logs, setLogs] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // info | error | success
  const [voiceDraft, setVoiceDraft] = useState({ label: "", phrase: "" });
  const [recordingVoice, setRecordingVoice] = useState(false);
  const [faceLabel, setFaceLabel] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [capturedImage, setCapturedImage] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [activeTab, setActiveTab] = useState("settings");
  const [saving, setSaving] = useState(false);
  const [commandBrain, setCommandBrain] = useState(mergeCommandBrain());
  const [trainingDraft, setTrainingDraft] = useState({
    category: "strategyTerms",
    term: "",
    label: "",
    path: "",
    action: "",
    aliases: "",
    meaning: "",
  });

  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const notify = (msg, type = "info") => { setMessage(msg); setMessageType(type); };

  // ── Config ──────────────────────────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/nexo-assistant/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (data.success) {
      setSettings(data.settings || {});
      setVoiceProfiles(data.voiceProfiles || []);
      setFaceProfiles(data.faceProfiles || []);
    }
  }, [token]);

  const loadLogs = useCallback(async () => {
    if (!isAdmin) return;
    const [logsRes, scansRes] = await Promise.all([
      fetch(`${API_BASE}/api/nexo-assistant/logs`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_BASE}/api/nexo-assistant/face-scans`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    const logsData = await logsRes.json().catch(() => ({}));
    const scansData = await scansRes.json().catch(() => ({}));
    if (logsData.success) setLogs(logsData.logs || []);
    if (scansData.success) setFaceScans(scansData.scans || []);
  }, [isAdmin, token]);

  const loadCommandBrain = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/nexo-assistant/command-brain`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (data.success) setCommandBrain(mergeCommandBrain(data.commandBrain || {}));
  }, [token]);

  useEffect(() => { loadConfig(); loadLogs(); loadCommandBrain(); }, [loadConfig, loadLogs, loadCommandBrain]);

  // ── Settings ────────────────────────────────────────────────────────────────
  const saveSettings = async () => {
    setSaving(true);
    const res = await fetch(`${API_BASE}/api/nexo-assistant/settings`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify(settings),
    });
    const data = await res.json().catch(() => ({}));
    setSaving(false);
    if (data.success) { setSettings(data.settings); notify("Settings saved successfully.", "success"); }
    else notify(data.error || "Failed to save settings.", "error");
  };

  const toggleVoiceToneLock = async () => {
    if (!isAdmin) return;
    const nextSettings = { ...settings, requireVoiceTone: settings.requireVoiceTone === false };
    setSettings(nextSettings);
    setSaving(true);
    const res = await fetch(`${API_BASE}/api/nexo-assistant/settings`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify(nextSettings),
    });
    const data = await res.json().catch(() => ({}));
    setSaving(false);
    if (data.success) {
      setSettings(data.settings);
      notify(data.settings.requireVoiceTone === false
        ? "Voice tone lock disabled. Nexo will accept any verified voice command."
        : "Voice tone lock enabled. Nexo will require enrolled voice tone.", "success");
    } else {
      notify(data.error || "Failed to update voice tone lock.", "error");
      await loadConfig();
    }
  };

  // ── Voice Profiles ──────────────────────────────────────────────────────────
  const addVoiceProfile = async () => {
    if (!voiceDraft.label.trim() || !voiceDraft.phrase.trim()) {
      notify("Both label and phrase are required.", "error"); return;
    }
    const res = await fetch(`${API_BASE}/api/nexo-assistant/voice-profiles`, {
      method: "POST", headers: authHeaders, body: JSON.stringify(voiceDraft),
    });
    const data = await res.json().catch(() => ({}));
    if (data.success) { setVoiceDraft({ label: "", phrase: "" }); await loadConfig(); notify("Voice profile added.", "success"); }
    else notify(data.error || "Failed to add voice profile.", "error");
  };

  const recordVoiceTone = async () => {
    if (!voiceDraft.label.trim() || !voiceDraft.phrase.trim()) {
      notify("Enter label and passphrase first, then record your tone.", "error"); return;
    }
    setRecordingVoice(true);
    notify(`Recording voice tone. Say clearly: ${voiceDraft.phrase}`, "info");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      voiceStreamRef.current = stream;
      const voiceprint = await captureVoiceprint(stream, 3200);
      setVoiceDraft(s => ({ ...s, voiceprint }));
      notify("Voice tone captured. Click Add to save this voice profile.", "success");
    } catch (err) {
      notify(`Voice recording failed: ${err.message}`, "error");
    } finally {
      voiceStreamRef.current?.getTracks?.().forEach(track => track.stop());
      voiceStreamRef.current = null;
      setRecordingVoice(false);
    }
  };

  const toggleVoiceProfile = async (profileId, active) => {
    await fetch(`${API_BASE}/api/nexo-assistant/voice-profiles/${profileId}`, {
      method: "PUT", headers: authHeaders, body: JSON.stringify({ active }),
    });
    await loadConfig();
  };

  const deleteVoiceProfile = async (profileId) => {
    await fetch(`${API_BASE}/api/nexo-assistant/voice-profiles/${profileId}`, {
      method: "DELETE", headers: authHeaders,
    });
    await loadConfig();
    notify("Voice profile removed.", "info");
  };

  // ── Camera ──────────────────────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraOn(true);
      notify("Camera started. Center your face then click Capture.", "info");
    } catch (err) {
      setCameraOn(false);
      notify(`Camera error: ${err.message}`, "error");
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks?.().forEach(t => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  // ── Frame Capture ────────────────────────────────────────────────────────────
  const captureFrame = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      notify("Camera frame not ready. Wait a moment and try again.", "error");
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL("image/jpeg", 0.82);
    const result = await analyzeFaceAndEyes(canvas);
    setCapturedImage(image);
    setScanResult(result);
    notify(`Captured — face ${result.faceScore}% / eyes ${result.eyeScore}%`, "success");
    return { image, ...result };
  };

  // ── Save Face Scan ───────────────────────────────────────────────────────────
  const saveFaceScan = async () => {
    const scan = capturedImage && scanResult
      ? { image: capturedImage, ...scanResult }
      : await captureFrame();
    if (!scan) return;

    const res = await fetch(`${API_BASE}/api/nexo-assistant/face-scans`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        image: scan.image,
        faceScore: scan.faceScore,
        eyeScore: scan.eyeScore,
        status: scan.status,
        detail: scan.detail,
        route: window.location.pathname,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (data.success) { notify("Face and eyes scan saved successfully.", "success"); await loadLogs(); }
    else notify(data.error || "Failed to save scan.", "error");
  };

  // ── Save Face Profile ────────────────────────────────────────────────────────
  const saveFaceProfile = async () => {
    if (!faceLabel.trim()) { notify("Enter a label for this face profile.", "error"); return; }
    const scan = capturedImage && scanResult
      ? { image: capturedImage, ...scanResult }
      : await captureFrame();
    if (!scan) return;

    const res = await fetch(`${API_BASE}/api/nexo-assistant/face-profiles`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ label: faceLabel, image: scan.image }),
    });
    const data = await res.json().catch(() => ({}));
    if (data.success) {
      setFaceLabel("");
      setCapturedImage("");
      setScanResult(null);
      stopCamera();
      await loadConfig();
      notify("Face profile enrolled successfully.", "success");
    } else notify(data.error || "Failed to save face profile.", "error");
  };

  const deleteFaceProfile = async (profileId) => {
    await fetch(`${API_BASE}/api/nexo-assistant/face-profiles/${profileId}`, {
      method: "DELETE", headers: authHeaders,
    });
    await loadConfig();
    notify("Face profile removed.", "info");
  };

  const speakTest = () => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(settings.bossGreeting || "Hello Boss. Nexo is online."));
  };

  const saveTrainingTerm = async () => {
    if (!isAdmin) return;
    const category = trainingDraft.category;
    const needsPage = category === "pageAliases";
    const needsAction = category === "actionAliases";
    if (needsPage && (!trainingDraft.label.trim() || !trainingDraft.path.trim())) {
      notify("Page label and path are required.", "error"); return;
    }
    if (needsAction && !trainingDraft.action.trim()) {
      notify("Action name is required.", "error"); return;
    }
    if (!needsPage && !needsAction && !trainingDraft.term.trim()) {
      notify("Training term is required.", "error"); return;
    }

    const res = await fetch(`${API_BASE}/api/nexo-assistant/command-brain/terms`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify(trainingDraft),
    });
    const data = await res.json().catch(() => ({}));
    if (data.success) {
      setCommandBrain(mergeCommandBrain(data.commandBrain || {}));
      setTrainingDraft(s => ({ ...s, term: "", label: "", path: "", action: "", aliases: "", meaning: "" }));
      notify("Training term saved. Nexo will use it on the next command cycle.", "success");
    } else notify(data.error || "Failed to save training term.", "error");
  };

  const tabs = [
    { id: "settings", label: "Operating Rules", icon: <Settings2 size={15} /> },
    { id: "voice", label: "Voice Profiles", icon: <Mic size={15} /> },
    { id: "face", label: "Face & Eyes", icon: <ScanFace size={15} /> },
    { id: "logs", label: "Command Audit", icon: <Activity size={15} /> },
    { id: "scans", label: "Scan History", icon: <Eye size={15} /> },
    { id: "training", label: "Training", icon: <Layers size={15} /> },
    { id: "security", label: "Security & Learning", icon: <ShieldCheck size={15} /> },
  ];

  const noticeColors = {
    info: { bg: "#0c1a2e", border: "#1d4ed8", text: "#93c5fd" },
    success: { bg: "#052e16", border: "#166534", text: "#bbf7d0" },
    error: { bg: "#2d0a0a", border: "#991b1b", text: "#fca5a5" },
  };
  const nc = noticeColors[messageType] || noticeColors.info;

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <section style={styles.header}>
        <div>
          <div style={styles.kicker}>Institutional Voice Control Desk</div>
          <h1 style={styles.title}>Nexo Assistant</h1>
          <p style={styles.subtitle}>
            Institutional-grade voice command surface. Navigate pages, type, click, submit, read content,
            and manage biometric profiles — all hands-free with multi-voice support and face/eye scanning.
          </p>
        </div>
        <div style={styles.statusCard}>
          <Bot size={28} color="#22c55e" />
          <span style={{ fontWeight: 800, fontSize: 18 }}>{settings.assistantName || "Nexo"}</span>
          <div style={{ display: "grid", gap: 4, fontSize: 12, color: "#94a3b8" }}>
            <span><UserCheck size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />{voiceProfiles.length} voice profiles</span>
            <span><ScanFace size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />{faceProfiles.length} face profiles</span>
            <span><Layers size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />Mode: {settings.commandMode || "confirmed"}</span>
          </div>
        </div>
      </section>

      {message && (
        <div style={{ border: `1px solid ${nc.border}`, background: nc.bg, color: nc.text, borderRadius: 7, padding: "10px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{message}</span>
          <button onClick={() => setMessage("")} style={{ background: "none", border: 0, color: nc.text, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ── Tab Bar ── */}
      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{ ...styles.tabBtn, ...(activeTab === tab.id ? styles.tabActive : {}) }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Settings ── */}
      {activeTab === "settings" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><ShieldCheck size={20} /><h2 style={styles.panelTitle}>Operating Rules</h2></div>

          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Assistant name</label>
              <input style={styles.input} value={settings.assistantName || ""} disabled={!isAdmin}
                onChange={e => setSettings(s => ({ ...s, assistantName: e.target.value }))} />
            </div>
            <div>
              <label style={styles.label}>Greeting message</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={styles.input} value={settings.bossGreeting || ""} disabled={!isAdmin}
                  onChange={e => setSettings(s => ({ ...s, bossGreeting: e.target.value }))} />
                <button style={styles.iconButton} onClick={speakTest} title="Test greeting"><Volume2 size={17} /></button>
              </div>
            </div>
            <div>
              <label style={styles.label}>Command mode</label>
              <select style={styles.input} value={settings.commandMode || "confirmed"} disabled={!isAdmin}
                onChange={e => setSettings(s => ({ ...s, commandMode: e.target.value }))}>
                <option value="confirmed">Confirm sensitive commands (recommended)</option>
                <option value="instant">Instant execution</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Read-out character limit ({settings.readLimit || 1200})</label>
              <input style={styles.input} type="number" min="300" max="4000" value={settings.readLimit || 1200} disabled={!isAdmin}
                onChange={e => setSettings(s => ({ ...s, readLimit: Number(e.target.value) }))} />
            </div>
          </div>

          <div style={{ display: "grid", gap: 12, margin: "18px 0", color: "#cbd5e1", fontSize: 13 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={settings.requireVoiceProfile !== false} disabled={!isAdmin}
                onChange={e => setSettings(s => ({ ...s, requireVoiceProfile: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <div>
                <strong>Require voice phrase verification</strong>
                <div style={{ color: "#64748b", fontSize: 12 }}>Nexo will not accept commands until the passphrase is spoken</div>
              </div>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={settings.requireVoiceTone !== false} disabled={!isAdmin}
                onChange={e => setSettings(s => ({ ...s, requireVoiceTone: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <div>
                <strong>Require enrolled voice tone</strong>
                <div style={{ color: "#64748b", fontSize: 12 }}>Nexo compares live mic tone with saved voice profiles before taking commands</div>
              </div>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={settings.requireFaceScan === true} disabled={!isAdmin}
                onChange={e => setSettings(s => ({ ...s, requireFaceScan: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <div>
                <strong>Auto face + eyes scan on agent start</strong>
                <div style={{ color: "#64748b", fontSize: 12 }}>Captures and saves a biometric scan when Nexo starts</div>
              </div>
            </label>
          </div>

          {isAdmin && (
            <button style={{ ...styles.primaryButton, opacity: saving ? 0.7 : 1 }} onClick={saveSettings} disabled={saving}>
              <Save size={16} /> {saving ? "Saving…" : "Save Settings"}
            </button>
          )}
        </section>
      )}

      {/* ── Tab: Voice Profiles ── */}
      {activeTab === "voice" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><Mic size={20} /><h2 style={styles.panelTitle}>Voice Profiles</h2></div>
          <p style={styles.muted}>
            Each profile holds a unique passphrase. When Nexo starts, it listens for any active passphrase before accepting commands.
            Multiple voices can be enrolled — Nexo responds to whichever phrase it hears first.
          </p>

          <div style={{ border: "1px solid #1f3a5f", borderRadius: 8, background: "#0c1728", padding: 12, marginBottom: 16, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "grid", gap: 3 }}>
              <strong style={{ fontSize: 13 }}>Voice tone detection</strong>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>
                {settings.requireVoiceTone === false
                  ? "Disabled: Nexo can accept commands from any voice after normal verification."
                  : "Enabled: Nexo accepts commands only when live tone matches an enrolled voice profile."}
              </span>
            </div>
            {isAdmin && (
              <button
                style={{
                  ...styles.secondaryButton,
                  background: settings.requireVoiceTone === false ? "#1e293b" : "#065f46",
                  opacity: saving ? 0.7 : 1,
                }}
                onClick={toggleVoiceToneLock}
                disabled={saving}
              >
                <ShieldCheck size={15} /> {settings.requireVoiceTone === false ? "Enable Tone Lock" : "Disable Tone Lock"}
              </button>
            )}
          </div>

          {isAdmin && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto auto", gap: 10, marginBottom: 18 }}>
              <input style={styles.input} placeholder="Profile label e.g. Boss primary"
                value={voiceDraft.label} onChange={e => setVoiceDraft(s => ({ ...s, label: e.target.value }))} />
              <input style={styles.input} placeholder="Verification passphrase"
                value={voiceDraft.phrase} onChange={e => setVoiceDraft(s => ({ ...s, phrase: e.target.value }))} />
              <button style={styles.secondaryButton} onClick={recordVoiceTone} disabled={recordingVoice}>
                <Volume2 size={15} /> {recordingVoice ? "Recording" : voiceDraft.voiceprint ? "Tone Saved" : "Record Tone"}
              </button>
              <button style={styles.primaryButton} onClick={addVoiceProfile}><Mic size={15} /> Add</button>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {voiceProfiles.map(profile => (
              <div key={profile.id} style={{ ...styles.listItem, opacity: profile.active === false ? 0.5 : 1 }}>
                <div style={{ display: "grid", gap: 3 }}>
                  <strong style={{ fontSize: 14 }}>{profile.label}</strong>
                  <code style={{ background: "#030712", border: "1px solid #243b5a", borderRadius: 4, padding: "2px 8px", fontSize: 12, color: "#38bdf8" }}>
                    {profile.phrase}
                  </code>
                  <small style={{ color: "#475569" }}>Added {new Date(profile.createdAt).toLocaleDateString()} by {profile.createdBy}</small>
                  <small style={{ color: profile.voiceprint ? "#4ade80" : "#fbbf24" }}>
                    {profile.voiceprint ? "Voice tone enrolled" : "No voice tone sample"}
                  </small>
                </div>
                {isAdmin && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ ...styles.iconButton, background: profile.active !== false ? "#065f46" : "#1e293b" }}
                      onClick={() => toggleVoiceProfile(profile.id, profile.active === false)}
                      title={profile.active !== false ? "Deactivate" : "Activate"}>
                      <ShieldCheck size={14} />
                    </button>
                    <button style={{ ...styles.iconButton, background: "#7f1d1d" }}
                      onClick={() => deleteVoiceProfile(profile.id)} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {!voiceProfiles.length && <div style={styles.empty}>No voice profiles enrolled yet.</div>}
          </div>
        </section>
      )}

      {/* ── Tab: Face & Eyes ── */}
      {activeTab === "face" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><ScanFace size={20} /><h2 style={styles.panelTitle}>Face & Eyes Biometric Enrollment</h2></div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <div>
              <video ref={videoRef} autoPlay muted playsInline
                style={{ width: "100%", height: 200, objectFit: "cover", background: "#030712", border: "1px solid #243b5a", borderRadius: 8, marginBottom: 10 }} />

              {capturedImage && (
                <div style={{ display: "flex", gap: 10, alignItems: "center", border: "1px solid #1f3a5f", borderRadius: 6, background: "#0c1728", padding: 10, marginBottom: 10 }}>
                  <img src={capturedImage} alt="Captured" style={{ width: 80, height: 56, objectFit: "cover", borderRadius: 4, border: "1px solid #243b5a" }} />
                  <div>
                    <strong style={{ display: "block", fontSize: 13 }}>Frame captured</strong>
                    {scanResult && (
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>
                        Face {scanResult.faceScore}% / Eyes {scanResult.eyeScore}%
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <button style={styles.secondaryButton} onClick={cameraOn ? stopCamera : startCamera}>
                  <Camera size={15} /> {cameraOn ? "Stop Camera" : "Start Camera"}
                </button>
                <button style={styles.secondaryButton} onClick={captureFrame}>
                  <Eye size={15} /> Capture Scan
                </button>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button style={styles.primaryButton} onClick={saveFaceScan}>
                  <Save size={15} /> Save Scan
                </button>
                {isAdmin && (
                  <>
                    <input style={{ ...styles.input, flex: 1 }} placeholder="Profile label for enrollment"
                      value={faceLabel} onChange={e => setFaceLabel(e.target.value)} />
                    <button style={styles.primaryButton} onClick={saveFaceProfile}>
                      <ShieldCheck size={15} /> Enroll Profile
                    </button>
                  </>
                )}
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 12px" }}>Enrolled Face Profiles</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}>
                {faceProfiles.map(profile => (
                  <div key={profile.id} style={{ border: "1px solid #1f3a5f", borderRadius: 8, padding: 8, background: "#0c1728", display: "grid", gap: 6 }}>
                    <img src={profile.image} alt={profile.label}
                      style={{ width: "100%", height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid #243b5a" }} />
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{profile.label}</span>
                    <small style={{ color: "#475569", fontSize: 10 }}>{new Date(profile.createdAt).toLocaleDateString()}</small>
                    {isAdmin && (
                      <button onClick={() => deleteFaceProfile(profile.id)}
                        style={{ border: 0, borderRadius: 4, background: "#7f1d1d", color: "#fff", padding: "4px 6px", fontSize: 11, cursor: "pointer" }}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                {!faceProfiles.length && <div style={styles.empty}>No face profiles enrolled.</div>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Tab: Command Audit ── */}
      {activeTab === "logs" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><Activity size={20} /><h2 style={styles.panelTitle}>Command Audit Log</h2></div>

          {!isAdmin && <div style={styles.empty}>Command audit is admin-only.</div>}
          {isAdmin && (
            <div style={{ display: "grid", gap: 8 }}>
              {logs.map(log => (
                <div key={log.id} style={{ border: "1px solid #1f3a5f", background: "#0c1728", borderRadius: 6, padding: "10px 14px", display: "grid", gap: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: log.status === "done" ? "#4ade80" : log.status === "failed" ? "#f87171" : "#fbbf24" }}>
                      {log.intent} / {log.status}
                    </span>
                    <small style={{ color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
                      <Clock size={11} /> {new Date(log.createdAt).toLocaleString()}
                    </small>
                  </div>
                  <span style={{ fontSize: 13, color: "#cbd5e1", fontStyle: "italic" }}>"{log.transcript}"</span>
                  {log.detail && <span style={{ fontSize: 11, color: "#64748b" }}>{log.detail}</span>}
                  <small style={{ color: "#334155" }}>by {log.user?.username} on {log.route}</small>
                </div>
              ))}
              {!logs.length && <div style={styles.empty}>No command logs yet.</div>}
            </div>
          )}
        </section>
      )}

      {/* ── Tab: Scan History ── */}
      {activeTab === "scans" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><Eye size={20} /><h2 style={styles.panelTitle}>Face / Eyes Scan History</h2></div>

          {!isAdmin && <div style={styles.empty}>Scan history is admin-only.</div>}
          {isAdmin && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
              {faceScans.map(scan => (
                <div key={scan.id} style={{ border: "1px solid #1f3a5f", borderRadius: 8, padding: 10, background: "#0c1728", display: "grid", gap: 6 }}>
                  <img src={scan.image} alt="Scan" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 6, border: "1px solid #243b5a" }} />
                  <span style={{ fontWeight: 600, fontSize: 12, color: "#22c55e" }}>{scan.status}</span>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>Face {scan.faceScore}% / Eyes {scan.eyeScore}%</span>
                  <small style={{ color: "#475569", fontSize: 10 }}>{new Date(scan.createdAt).toLocaleString()}</small>
                  <small style={{ color: "#334155", fontSize: 10 }}>{scan.user?.username}</small>
                </div>
              ))}
              {!faceScans.length && <div style={styles.empty}>No face/eyes scans saved yet.</div>}
            </div>
          )}
        </section>
      )}

      {/* ── Tab: Training ── */}
      {activeTab === "training" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><Layers size={20} /><h2 style={styles.panelTitle}>Command Dictionary Training</h2></div>
          <p style={styles.muted}>
            Add your page nicknames, action words, and strategy terms. The floating Nexo agent reads this dictionary while navigating,
            clicking start/stop controls, and reading exact data from any active page.
          </p>

          {!isAdmin && <div style={styles.empty}>Training is admin-only.</div>}
          {isAdmin && (
            <div style={{ display: "grid", gap: 14, marginBottom: 18 }}>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>Training type</label>
                  <select style={styles.input} value={trainingDraft.category}
                    onChange={e => setTrainingDraft(s => ({ ...s, category: e.target.value }))}>
                    <option value="strategyTerms">Strategy term</option>
                    <option value="pageAliases">Page alias</option>
                    <option value="actionAliases">Action alias</option>
                    <option value="learnedTerms">General learned term</option>
                  </select>
                </div>

                {trainingDraft.category === "pageAliases" ? (
                  <>
                    <div>
                      <label style={styles.label}>Page label</label>
                      <input style={styles.input} placeholder="Expiry Decay Desk"
                        value={trainingDraft.label} onChange={e => setTrainingDraft(s => ({ ...s, label: e.target.value }))} />
                    </div>
                    <div>
                      <label style={styles.label}>Page path</label>
                      <input style={styles.input} placeholder="/expiry-decay-desk"
                        value={trainingDraft.path} onChange={e => setTrainingDraft(s => ({ ...s, path: e.target.value }))} />
                    </div>
                  </>
                ) : trainingDraft.category === "actionAliases" ? (
                  <div>
                    <label style={styles.label}>Action name</label>
                    <input style={styles.input} placeholder="start, stop, read, click, navigate"
                      value={trainingDraft.action} onChange={e => setTrainingDraft(s => ({ ...s, action: e.target.value }))} />
                  </div>
                ) : (
                  <div>
                    <label style={styles.label}>Term</label>
                    <input style={styles.input} placeholder="Call sellers, liquidity sweep, breakout strength"
                      value={trainingDraft.term} onChange={e => setTrainingDraft(s => ({ ...s, term: e.target.value }))} />
                  </div>
                )}

                <div>
                  <label style={styles.label}>Aliases</label>
                  <input style={styles.input} placeholder="Comma separated voice phrases"
                    value={trainingDraft.aliases} onChange={e => setTrainingDraft(s => ({ ...s, aliases: e.target.value }))} />
                </div>
                <div>
                  <label style={styles.label}>Meaning / notes</label>
                  <input style={styles.input} placeholder="What Nexo should treat this as"
                    value={trainingDraft.meaning} onChange={e => setTrainingDraft(s => ({ ...s, meaning: e.target.value }))} />
                </div>
              </div>

              <button style={styles.primaryButton} onClick={saveTrainingTerm}>
                <Save size={15} /> Save Training
              </button>
            </div>
          )}

          <div style={styles.trainingStats}>
            <div style={styles.trainingStat}><strong>{commandBrain.pageAliases.length}</strong><span>page aliases</span></div>
            <div style={styles.trainingStat}><strong>{commandBrain.actionAliases.length}</strong><span>action groups</span></div>
            <div style={styles.trainingStat}><strong>{commandBrain.strategyTerms.length}</strong><span>strategy terms</span></div>
            <div style={styles.trainingStat}><strong>{commandBrain.learnedTerms.length}</strong><span>manual terms</span></div>
            <div style={styles.trainingStat}><strong>{commandBrain.pageSnapshots.length}</strong><span>self-learned pages</span></div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 16 }}>
            <DictionaryList title="Page Aliases" items={commandBrain.pageAliases} primaryKey="label" secondaryKey="path" />
            <DictionaryList title="Action Aliases" items={commandBrain.actionAliases} primaryKey="action" />
            <DictionaryList title="Strategy Terms" items={commandBrain.strategyTerms} primaryKey="term" secondaryKey="meaning" />
          </div>
        </section>
      )}

      {activeTab === "security" && (
        <section style={styles.panel}>
          <div style={styles.panelHead}><ShieldCheck size={20} /><h2 style={styles.panelTitle}>Security Anomalies & Vocabulary Learning</h2></div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>Anomaly Log</h3>
                {isAdmin && (
                  <button onClick={() => { clearAnomalyLog(); setMessage("Anomaly log cleared."); }}
                    style={{ ...styles.iconButton, width: "auto", padding: "4px 10px", fontSize: 11 }}>
                    Clear
                  </button>
                )}
              </div>
              <div style={{ display: "grid", gap: 8, maxHeight: 400, overflowY: "auto" }}>
                {getAnomalyLog().reverse().map((item, i) => (
                  <div key={i} style={{ border: "1px solid #92400e", background: "#451a03", borderRadius: 6, padding: "8px 12px" }}>
                    <strong style={{ fontSize: 12, color: "#fbbf24" }}>{item.type}</strong>
                    <div style={{ fontSize: 12, color: "#fde68a", marginTop: 2 }}>{item.detail}</div>
                    <small style={{ color: "#92400e" }}>{new Date(item.at).toLocaleString()}</small>
                  </div>
                ))}
                {!getAnomalyLog().length && <div style={styles.empty}>No anomalies detected.</div>}
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 10px" }}>Suggested Vocabulary (not yet trained)</h3>
              <p style={{ color: "#64748b", fontSize: 12, marginTop: 0 }}>
                Words Nexo hears often in successful commands but hasn't been explicitly trained on. Consider adding these as training terms.
              </p>
              <div style={{ display: "grid", gap: 6 }}>
                {getTopUnrecognisedCandidates(
                  [...commandBrain.pageAliases.flatMap(p => [p.label, ...(p.aliases || [])]),
                   ...commandBrain.actionAliases.flatMap(a => [a.action, ...(a.aliases || [])]),
                   ...commandBrain.strategyTerms.flatMap(s => [s.term, ...(s.aliases || [])])]
                ).map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", border: "1px solid #1f3a5f", borderRadius: 6, padding: "6px 10px", background: "#0c1728" }}>
                    <span style={{ fontSize: 12, color: "#e5eefb" }}>{item.word}</span>
                    <span style={{ fontSize: 11, color: "#38bdf8" }}>{item.count}× used</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function DictionaryList({ title, items, primaryKey, secondaryKey }) {
  return (
    <div style={{ border: "1px solid #1f3a5f", borderRadius: 8, background: "#0c1728", padding: 12, display: "grid", gap: 8, alignContent: "start" }}>
      <h3 style={{ margin: 0, fontSize: 13, color: "#bfdbfe" }}>{title}</h3>
      {(items || []).slice(0, 12).map((item, index) => (
        <div key={item.id || `${item[primaryKey]}-${index}`} style={{ borderTop: "1px solid #152a46", paddingTop: 8, display: "grid", gap: 3 }}>
          <strong style={{ fontSize: 12 }}>{item[primaryKey]}</strong>
          {secondaryKey && item[secondaryKey] && <small style={{ color: "#64748b" }}>{item[secondaryKey]}</small>}
          {!!item.aliases?.length && <small style={{ color: "#38bdf8" }}>{item.aliases.slice(0, 8).join(", ")}</small>}
        </div>
      ))}
      {!items?.length && <div style={styles.empty}>No entries yet.</div>}
    </div>
  );
}

async function captureVoiceprint(stream, durationMs = 3000) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) throw new Error("AudioContext is not supported in this browser");
  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.25;
  source.connect(analyser);

  const time = new Uint8Array(analyser.fftSize);
  const freq = new Uint8Array(analyser.frequencyBinCount);
  const samples = [];
  const started = Date.now();

  while (Date.now() - started < durationMs) {
    analyser.getByteTimeDomainData(time);
    analyser.getByteFrequencyData(freq);
    samples.push(extractVoiceFeatures(time, freq, ctx.sampleRate));
    await new Promise(resolve => setTimeout(resolve, 90));
  }

  await ctx.close().catch(() => {});
  return averageVoiceFeatures(samples);
}

function extractVoiceFeatures(time, freq, sampleRate) {
  let rms = 0;
  let crossings = 0;
  for (let i = 0; i < time.length; i++) {
    const v = (time[i] - 128) / 128;
    rms += v * v;
    if (i > 0 && (time[i - 1] - 128) * (time[i] - 128) < 0) crossings++;
  }
  rms = Math.sqrt(rms / time.length);

  let sum = 0;
  let weighted = 0;
  const bands = [0, 0, 0, 0, 0, 0];
  for (let i = 0; i < freq.length; i++) {
    const value = freq[i] / 255;
    const hz = (i * sampleRate) / (2 * freq.length);
    sum += value;
    weighted += hz * value;
    const band = Math.min(bands.length - 1, Math.floor(i / (freq.length / bands.length)));
    bands[band] += value;
  }
  const bandTotal = bands.reduce((a, b) => a + b, 0) || 1;
  return {
    rms,
    zcr: crossings / time.length,
    centroid: sum ? weighted / sum : 0,
    bands: bands.map(v => v / bandTotal),
  };
}

function averageVoiceFeatures(samples) {
  const usable = samples.filter(s => s.rms > 0.01);
  const list = usable.length ? usable : samples;
  const base = { rms: 0, zcr: 0, centroid: 0, bands: [0, 0, 0, 0, 0, 0] };
  list.forEach(sample => {
    base.rms += sample.rms;
    base.zcr += sample.zcr;
    base.centroid += sample.centroid;
    sample.bands.forEach((v, i) => { base.bands[i] += v; });
  });
  const count = Math.max(1, list.length);
  return {
    version: 1,
    createdAt: new Date().toISOString(),
    rms: Number((base.rms / count).toFixed(4)),
    zcr: Number((base.zcr / count).toFixed(4)),
    centroid: Math.round(base.centroid / count),
    bands: base.bands.map(v => Number((v / count).toFixed(4))),
  };
}

// ── Face Analysis (same robust fallback as agent) ────────────────────────────
async function analyzeFaceAndEyes(canvas) {
  if ("FaceDetector" in window) {
    try {
      const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
      const faces = await detector.detect(canvas);
      if (faces.length > 0) {
        const landmarks = faces[0].landmarks || [];
        const eyes = landmarks.filter(l => String(l.type || "").toLowerCase().includes("eye"));
        return {
          faceScore: 96,
          eyeScore: eyes.length >= 2 ? 94 : 70,
          status: eyes.length >= 2 ? "face_eyes_detected" : "face_detected",
          detail: eyes.length >= 2 ? "Face and eye landmarks detected." : "Face detected, eye landmarks estimated.",
        };
      }
    } catch { /* fallthrough */ }
  }
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const step = Math.max(4, Math.floor(img.data.length / 40000));
  let brightness = 0, contrast = 0, n = 0;
  for (let i = 0; i < img.data.length; i += 4 * step) {
    const lum = (img.data[i] + img.data[i + 1] + img.data[i + 2]) / 3;
    brightness += lum;
    contrast += Math.abs(lum - 128);
    n++;
  }
  brightness = n ? brightness / n : 0;
  contrast = n ? contrast / n : 0;
  const quality = Math.max(0, Math.min(100, Math.round((brightness / 255) * 55 + Math.min(contrast, 80) * 0.55)));
  return {
    faceScore: quality,
    eyeScore: Math.max(0, quality - 12),
    status: quality >= 40 ? "frame_captured" : "low_light",
    detail: "Native face detector unavailable. Frame quality scoring used.",
  };
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: { minHeight: "100vh", background: "#050914", color: "#e5eefb", padding: "28px 32px", fontFamily: "Inter, system-ui, sans-serif" },
  header: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 260px", gap: 24, alignItems: "stretch", marginBottom: 24 },
  kicker: { color: "#38bdf8", fontSize: 11, textTransform: "uppercase", letterSpacing: 2.5, fontWeight: 800 },
  title: { fontSize: 40, lineHeight: 1.1, margin: "8px 0 10px", letterSpacing: "-0.5px" },
  subtitle: { color: "#94a3b8", maxWidth: 720, lineHeight: 1.6, margin: 0, fontSize: 14 },
  statusCard: { border: "1px solid #1f3a5f", borderRadius: 10, background: "#0c1728", padding: 20, display: "grid", gap: 10, alignContent: "center" },
  tabBar: { display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" },
  tabBtn: { border: "1px solid #1f3a5f", borderRadius: 7, background: "#0c1728", color: "#64748b", padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, transition: "all 0.2s" },
  tabActive: { border: "1px solid #2563eb", background: "#1e3a5f", color: "#bfdbfe" },
  panel: { border: "1px solid #1f3a5f", borderRadius: 10, background: "#08111f", padding: 22 },
  panelHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 18 },
  panelTitle: { fontSize: 17, fontWeight: 800, margin: 0 },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  label: { display: "block", color: "#94a3b8", fontSize: 12, margin: "0 0 6px", fontWeight: 600 },
  input: { width: "100%", background: "#030712", border: "1px solid #243b5a", color: "#e5eefb", borderRadius: 7, padding: "10px 12px", outline: "none", fontSize: 13, boxSizing: "border-box" },
  primaryButton: { border: 0, borderRadius: 7, background: "#16a34a", color: "#fff", padding: "10px 14px", fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", fontSize: 13 },
  secondaryButton: { border: "1px solid #334155", borderRadius: 7, background: "#1e293b", color: "#fff", padding: "10px 13px", fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", fontSize: 13 },
  iconButton: { border: "1px solid #334155", borderRadius: 6, background: "#1e293b", color: "#fff", width: 40, minWidth: 40, height: 40, display: "grid", placeItems: "center", cursor: "pointer" },
  muted: { color: "#94a3b8", lineHeight: 1.6, marginTop: 0, fontSize: 13 },
  listItem: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", border: "1px solid #1f3a5f", background: "#0c1728", borderRadius: 8, padding: "12px 14px", transition: "opacity 0.2s" },
  trainingStats: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 },
  trainingStat: { border: "1px solid #1f3a5f", background: "#0c1728", borderRadius: 8, padding: 12, display: "grid", gap: 4, color: "#94a3b8", fontSize: 12 },
  empty: { color: "#64748b", border: "1px dashed #334155", borderRadius: 7, padding: 16, fontSize: 13 },
};
